<template>
    <div class="studio-panel">
        <ToggleButton
            :isPanelHidden="!panelDisplayed"
            :isExportStep="isExportStep && !isDownloadStep"
            :isFilenameFilled="filename !== ''"
            @toggle-panel="toggleStudioPanel"
            @start-export="startExport"
        />

        <v-slide-x-reverse-transition>
            <v-card
                v-show="panelDisplayed"
                id="mediaExportForm"
                :width="400"
                :min-height="600"
            >
                <template v-if="stepsConfiguration && !isAPIError">
                    <template v-if="!isDownloading">
                        <StepSummary
                            v-if="!isDownloadStep"
                            :currentStep="step"
                            :steps="stepsConfiguration"
                            @select-step="selectStep"
                        />

                        <Steps
                            v-if="stepObject"
                            :selectedValues="selectedValues"
                            :step="stepObject"
                            :steps="stepsConfiguration"
                            :isDownloadStep="isDownloadStep"
                            :isDownloadSuccess="isDownloadSuccess"
                            @select-step="selectStep"
                            @update-disclaimer="updateDisclaimer"
                            @set-properties="setAdvancedPresetProperties"
                        />
                    </template>

                    <StepExportAnimation
                        v-else
                        :isDownloadComplete="isDownloadComplete"
                        @download-success="endDownload"
                    />

                    <StepFooter
                        v-if="stepObject"
                        :currentStep="step"
                        :isDownloading="isDownloading"
                        :isDownloadStep="isDownloadStep"
                        :isExportStep="isExportStep"
                        :isEditingStep="isEditingStep"
                        :maxStep="stepsConfiguration.length"
                        :step="stepObject"
                        @reset-form="resetForm"
                        @start-export="startExport"
                        @cancel-export="cancelExport"
                        @submit-summary="submitSummary"
                        @go-back-summary="goBackSummary"
                        @previous-step="previousStep"
                        @next-step="nextStep"
                    />
                </template>

                <template v-else-if="isAPIError">
                    <div class="d-flex flex-column pt-6">
                        <div
                            class="justify-center mt-12"
                            style="height: 150px;"
                        >
                            <v-img
                                :max-width="150"
                                :src="exportFailImg"
                            />
                        </div>
                        <div class="mt-6 px-6 text-center">
                            <span
                                class="h2 download-title orange-text text-darken-2"
                                v-html="$t('habiteoStudio.accessError')"
                            />
                        </div>
                    </div>
                </template>
            </v-card>
        </v-slide-x-reverse-transition>

        <!-- Missing filename snackbar -->
        <v-snackbar
            v-model="snackbar['filename']"
            class="snackbar"
            color="success"
            top
            :timeout="snackbarTimeout"
        >
            <v-layout class="justify-center">
                Veuillez renseigner un nom pour votre média
            </v-layout>
        </v-snackbar>
        <!-- /Missing filename snackbar -->
    </div>
</template>

<script>
    import _ from 'lodash';
    import { EventBus } from '@/event-bus';
    import Http from "@/api/data";
    import { mapGetters } from 'vuex';

    import ToggleButton from '@/components/myHabiteoStudio/components/ToggleButton';
    import Steps from '@/components/myHabiteoStudio/steps/Steps';
    import StepSummary from '@/components/myHabiteoStudio/components/StepSummary';
    import StepFooter from '@/components/myHabiteoStudio/components/StepFooter';
    import StepExportAnimation from '@/components/myHabiteoStudio/components/StepExportAnimation';

    export default {
        name: 'MyHabiteoStudio',
        components: {
            Steps,
            StepFooter,
            StepSummary,
            StepExportAnimation,
            ToggleButton
        },
        props: {
            formUrl: {
                type: String,
                required: true
            },
            zoomFactor: {
                type: Number,
                required: true
            },
            exportedBlobFile: {
                type: Blob,
                required: false
            }
        },
        data() {
            return {
                step: 1,
                exportForm: null,
                backupForm: null,
                isExportStep: false,
                isDownloading: false,
                snackbar: {
                    'filename': false
                },
                snackbarTimeout: 3000,
                isDownloadStep: false,
                disabled360Steps: [
                    'step_2',
                    'step_3'
                ],
                isEditingStep: false,
                filename: '',
                ignoredFields: [
                    'name',
                    'file'
                ],
                exportFailImg: require('@/assets/images/export-fail.png'),
                isPostFormSuccess: null,
                isAPIError: false,
                panelDisplayed: true,
                advancedProperties: {
                    'architectural': 0,
                    'pannini': 0,
                    'fisheye': 0.5,
                },
                mediaList: [
                    'image',
                    '360_pano_image'
                ],
                formatList: [
                    {'x': 1, 'y': 1},       // 1:1
                    {'x': 16, 'y': 9},      // 16:9
                    {'x': 3, 'y': 4},       // 3:4
                    {'x': 4, 'y': 3},       // 4:3
                    {'x': 3, 'y': 2}        // 3:2
                ],
                defaultPreset: {
                    'architectural': 0,
                    'pannini': 0,
                    'fisheye': 0
                },
                presetList: [
                    {'architectural': 0, 'pannini': 0, 'fisheye': 0, 'offset': 1},          // Initial
                    {'architectural': 0, 'pannini': 1, 'fisheye': 0.25, 'offset': .86},     // Natural angle
                    {'architectural': 0, 'pannini': 1, 'fisheye': 0.5, 'offset': .77},      // Medium Angle
                    {'architectural': 0, 'pannini': 1, 'fisheye': 1, 'offset': .68},        // Wide Angle
                    {'architectural': 1, 'pannini': 0, 'fisheye': 0, 'offset': 1}           // Straight Verticals
                ],
                oldPresetZoom: null,
                resolutionList: [
                    {'width': 1920, 'height': 1080},    // Full-HD
                    {'width': 3840, 'height': 2160}     // 4K
                ],
                currentZoomFactor: null
            }
        },
        computed: {
            ...mapGetters([
                'isIframe',
                'isInternal',
                'isLocalstorageAvailable'
            ]),
            stepsConfiguration() {
                return this.exportForm ? this.exportForm.steps.filter(step => step.active) : null;
            },
            stepObject() {
                return this.stepsConfiguration.length ? this.stepsConfiguration[this.step - 1] : null;
            },
            isDownloadComplete() {
                return this.$props.exportedBlobFile && this.$props.exportedBlobFile !== null ? true : false;
            },
            selectedValues() {
                let settings = {};
                if (this.stepsConfiguration)
                    this.stepsConfiguration.forEach(step => {
                        step.forms.forEach(form => {
                            form.inputs.forEach(input => {
                                settings[input.id] = input.value;
                            });
                        });
                    });
                return settings;
            },
            isAllStepFilled() {
                let status = true;
                Object.keys(this.selectedValues).forEach(key => {
                    if (!this.ignoredFields.includes(key) && this.selectedValues[key] === null)
                        status = false;
                });
                return status;
            },
            isDownloadSuccess() {
                return this.isDownloadComplete && this.isPostFormSuccess;
            },
            isExternal() {
                return this.isIframe && document.referrer === process.env.PROMOTER_SPACE;
            },
            hasCredentials() {
                return this.$route.query.id !== undefined && this.$route.query.key !== undefined && this.$route.query.userId !== undefined;
            }
        },
        mounted() {
            if (!this.hasCredentials && !this.isInternal)
                this.isAPIError = true;
            else {
                this.getForm();
                EventBus.$on('setFilename', filename => this.filename = filename);
                EventBus.$on('update-step', () => { this.isEditingStep = true; });
            }
        },
        methods: {
            setAdvancedPresetProperties(type, value){
                this.advancedProperties[type] = value;
                this.advancedProperties['fov'] =  1 - 0.32 * this.advancedProperties['fisheye'];
                this.$emit('set-advanced_preset', { 'properties': this.advancedProperties });
            },
            getForm() {
                Http.getData(this.$props.formUrl).then(payload => {
                    if (payload) {
                        this.exportForm = payload;
                        this.backupForm = _.cloneDeep(this.exportForm);

                        EventBus.$emit('updateSidebar', payload, false);
                        EventBus.$emit('trackWidget', payload.tracking);
                    }
                }).catch(error => {
                    console.log(error);
                    this.isAPIError = true;
                });
            },
            postForm() {
                if (this.hasCredentials && !this.isInternal) {
                    let config = {
                        'header' : {
                            'Content-Type' : 'multipart/form-data'
                        }
                    };

                    // Extract datas from exportForm.
                    let step1 = this.exportForm.steps.filter(step => step.id === 'step_1')[0];
                    let media = step1.forms[0].inputs.filter(input => input.id === 'media');

                    let step2 = this.exportForm.steps.filter(step => step.id === 'step_2')[0];
                    let format = step2.forms[0].inputs.filter(input => input.id === 'format');

                    let step3 = this.exportForm.steps.filter(step => step.id === 'step_3')[0];
                    let quality = step3.forms[1].inputs.filter(input => input.id === 'quality'); // forms[1] because hidden color field.
                    let preset = step3.forms[2].inputs.filter(input => input.id === 'preset')

                    let step4 = this.exportForm.steps.filter(step => step.id === 'step_4')[0];
                    let mention = step4.forms[0].inputs.filter(input => input.id === 'mention');

                    let step5 = this.exportForm.steps.filter(step => step.id === 'step_5')[0];
                    let name = step5.forms[0].inputs.filter(input => input.id === 'name');
                    
                    // format formData for POST request.
                    let formData = new FormData();
                    formData.append('media', media[0].value); // int key
                    formData.append('mention', mention[0].value); // bool
                    formData.append('name', name[0].value); // string

                    if (media[0].value === 1) {
                        formData.append('format', format[0].value || 0); // int key
                        formData.append('quality', quality[0].value || 0); // int key
                        formData.append('preset', preset[0].value || 0); // int key
                    }
                    // Handle userId.
                    if (this.$route.query.userId)
                        formData.append('user_id', this.$route.query.userId); // string

                    // Add file and send.
                    formData.append('file', this.$props.exportedBlobFile);
                    if (media[0].value === 1)
                        console.log('Sending formData : [media:' +media[0].value+ ', format:' +format[0].value+ ', quality:' +quality[0].value+ ', preset:' +preset[0].value+ ', mention:' +mention[0].value+ ', name:' +name[0].value+ ']');
                    else
                        console.log('Sending formData : [media:' +media[0].value+ ', mention:' +mention[0].value+ ', name:' +name[0].value+ ']');
                    Http.postData(this.$props.formUrl, formData, config).then(payload => {
                        console.log(payload);
                        this.isPostFormSuccess = true;
                        this.isDownloading = false;
                    }).catch(error => {
                        console.log('Error during export', error);
                        this.isPostFormSuccess = false;
                        this.isDownloading = false;
                    });
                } else {
                    this.isPostFormSuccess = true;
                    this.isDownloading = false;
                }
            },
            submitSummary() {
                if (this.isAllStepFilled) {
                    this.isExportStep = true;
                    EventBus.$emit('submit-summary');
                }
            },
            goBackSummary() {
                this.isExportStep = false;
                this.isEditingStep = false;
                this.step = this.stepsConfiguration.findIndex(step => step.id === 'step_5') + 1;
                EventBus.$emit('display-summary');
            },
            selectStep(step) {
                this.step = step;
            },
            nextStep() {
                this.step++;
            },
            previousStep() {
                if (this.isExportStep)
                    this.isExportStep = false;
                this.step--;
            },
            startExport() {
                if (this.filename) {
                    this.isDownloading = true;
                    this.$emit('export-media', {
                        'settings': {
                            'media': this.mediaList[this.selectedValues['media'] - 1],
                            'quality': this.resolutionList[this.selectedValues['quality'] - 1],
                            'format': this.formatList[this.selectedValues['format'] - 1]
                        },
                        'filename': this.filename
                    });
                }
                else
                    this.snackbar['filename'] = true;
            },
            endDownload() {
                this.isDownloadStep = true;
            },
            resetForm() {
                this.isDownloading = false;
                this.isDownloadStep = false;
                this.isExportStep = false;
                this.step = 1;
                this.filename = '';
                this.isPostFormSuccess = null;
                this.exportForm = _.cloneDeep(this.backupForm);
            },
            cancelExport() {
                this.isDownloading = false;
                EventBus.$emit('valid-summary');
            },
            blobToBase64(blob) {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                return new Promise(resolve => {
                    reader.onloadend = () => {
                        resolve(reader.result);
                    };
                });
            },
            toggleStudioPanel() {
                this.panelDisplayed = !this.panelDisplayed;
            },
            updateDisclaimer() {
                let optionsStep = this.exportForm.steps.filter(step => step.id === 'step_4')[0].forms[0].inputs[0];
                this.$emit('set-mention', {
                    'active': this.selectedValues['mention'],
                    'text': optionsStep.text || null,
                    'fontsize': optionsStep.fontsize || null,
                    'color': optionsStep.color || null
                });
            }
        },
        watch: {
            exportedBlobFile() {
                this.endDownload();
                this.postForm();
            },
            'selectedValues.media'(oldVal, newVal) {
                // Get form 'quality' object from JSON
                var formColor = this.exportForm.steps.filter(step => step.id === 'step_3')[0].forms.filter(form => form.id == 'color')[0];

                // Set RVB as quality color by default
                formColor.inputs[0].value = formColor.inputs[0].fields.filter(field => field.id === 'rvb')[0].value;

                // If media is 360 image
                if (this.selectedValues['media'] === 2) {
                    this.exportForm.steps.filter(step => step.id === 'step_2')[0].active = false;
                    this.exportForm.steps.filter(step => step.id === 'step_3')[0].active = false;
                } else {
                    this.exportForm.steps.filter(step => step.id === 'step_2')[0].active = true;
                    this.exportForm.steps.filter(step => step.id === 'step_3')[0].active = true;
                }

                if (typeof newVal !== 'undefined' && newVal !== null) {
                    if (newVal !== oldVal) {
                        // Reset format
                        this.exportForm.steps.find(step => step.id === 'step_2').forms.find(form => form.id === 'format').inputs[0].value = null;
                        // Reset preset
                        this.exportForm.steps.find(step => step.id === 'step_3').forms.find(form => form.id === 'preset').inputs[0].value = null;
                        // Reset disclaimer
                        this.exportForm.steps.find(step => step.id === 'step_4').forms.find(form => form.id === 'mention').inputs[0].value = null;
                    }
                }
            },
            'selectedValues.format'(oldVal, newVal) {
                if (newVal !== undefined)
                    this.$emit('set-format', this.formatList[this.selectedValues['format'] - 1]);
            },
            'selectedValues.preset'(oldVal, newVal) {
                if (newVal !== undefined && newVal !== null) {
                    var properties = {};
                    var oldZoomOffset = null;
                    var newZoomOffset = null;
                    if ((this.selectedValues['preset'] - 1) === this.presetList.length) {
                        properties = this.isLocalstorageAvailable ? JSON.parse(localStorage.getItem(`advanced-preset-${this.$route.query.id}`)) : {};
                        newZoomOffset =  1 - 0.32 * properties['fisheye'];
                        var oldOffset = newVal > this.presetList.length ? 1 : this.presetList[newVal -1]['offset'];
                        oldZoomOffset = oldOffset ? oldOffset : this.oldPresetZoom;
                        this.oldPresetZoom = newZoomOffset;
                        this.currentZoomFactor = this.currentZoomFactor / oldZoomOffset  * newZoomOffset;
                    } else if(this.selectedValues['preset']) {
                        properties = this.presetList[this.selectedValues['preset'] -1];
                        if (newVal && newVal !== null) {
                            oldZoomOffset = newVal > this.presetList.length ? 1 : this.presetList[newVal -1]['offset'];
                            newZoomOffset = this.selectedValues['preset'] > this.presetList.length ? 1 : this.presetList[this.selectedValues['preset'] -1]['offset'];
                            this.currentZoomFactor = this.currentZoomFactor / oldZoomOffset * newZoomOffset;
                        }  
                    }
                    properties['fov'] = this.currentZoomFactor;
                    this.$emit('set-preset', { 'properties': properties });
                } else if(oldVal !== null) {
                    Object.assign(this.defaultPreset, {'fov': this.currentZoomFactor });
                    this.$emit('set-preset', { 'properties': this.defaultPreset });
                }
            },
            'selectedValues.mention'(oldVal, newVal) {
                if (newVal !== undefined)
                    this.updateDisclaimer();
            },
            'selectedValues.quality'(oldVal, newVal) {
                if (newVal !== undefined)
                    this.$emit('set-quality', this.selectedValues['quality']);
            },
            zoomFactor(zoom) {
                if (zoom && this.currentZoomFactor === null)
                    this.currentZoomFactor = zoom;
            }
        }
    }
</script>

<style lang="scss">
    @import "@/assets/scss/components/_my-habiteo-studio.scss";
</style>


// WEBPACK FOOTER //
// src/components/myHabiteoStudio/MyHabiteoStudio.vue